<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-fade-in">
    <div class="bg-white dark:bg-slate-900 w-full max-w-7xl rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[95vh]">

      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
        <div class="flex items-center gap-4">
          <div class="p-2 bg-brand-600 rounded-xl shadow-lg shadow-brand-500/20">
            <Activity class="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 class="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tight">Estado de Cuenta Integral</h2>
            <p class="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">
              EXP: {{ loanData?.code || loanId }} • ESTATUS: {{ dossier?.loan?.status || 'CARGANDO...' }}
            </p>
          </div>
        </div>
        <div class="flex items-center gap-3">
          <button @click="handlePrint" class="p-2 text-slate-400 hover:text-brand-600 transition-colors tooltip-trigger" title="Imprimir Estado de Cuenta">
            <Printer class="w-5 h-5" />
          </button>
          <button @click="$emit('close')" class="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full text-slate-400 hover:text-red-500 transition-colors">
            <X class="w-6 h-6" />
          </button>
        </div>
      </div>

      <div class="flex overflow-x bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
        <button v-for="tab in tabs" :key="tab.id" @click="activeTab = tab.id"
          :class="['px-6 py-3 text-[10px] font-black uppercase tracking-widest whitespace-nowrap transition-all border-b-2',
          activeTab === tab.id ? 'border-brand-600 text-brand-600 bg-white dark:bg-slate-900' : 'border-transparent text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300']">
          {{ tab.name }}
        </button>
      </div>

      <div v-if="isLoadingDossier" class="flex-1 flex flex-col items-center justify-center p-20 bg-white dark:bg-slate-900">
        <Loader2 class="w-10 h-10 animate-spin text-brand-600 mb-4" />
        <p class="text-xs font-bold text-slate-400 uppercase tracking-widest">Sincronizando Expediente y Movimientos...</p>
      </div>

      <div v-else-if="dossier" class="flex-1 overflow-y-auto p-6 custom-scrollbar bg-slate-50/30 dark:bg-slate-950/20">

        <!-- 1. PERFIL CLIENTE -->
        <div v-show="activeTab === 'customer'" class="space-y-6 animate-fade-in">
          <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div class="lg:col-span-3 space-y-4">
              <div :class="sectionCardClasses">
                <label :class="labelClasses" class="text-center w-full mb-2">Foto de Perfil</label>
                <div class="w-32 h-32 rounded-2xl bg-slate-200 dark:bg-slate-700 mx-auto overflow-hidden border-2 border-white dark:border-slate-800 shadow-sm">
                  <img v-if="dossier.customer.photo_url" :src="dossier.customer.photo_url" class="w-full h-full object-cover" />
                  <User v-else class="w-full h-full p-6 text-slate-400" />
                </div>
                <div class="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
                  <label :class="labelClasses" class="text-center w-full mb-2">Firma del Titular</label>
                  <div class="bg-white rounded-xl p-2 border border-slate-200">
                    <img v-if="dossier.customer.signature_base64" :src="dossier.customer.signature_base64" class="max-h-16 mx-auto dark:invert-0" />
                  </div>
                </div>
              </div>
              <div :class="sectionCardClasses" class="space-y-3">
                <label :class="labelClasses">Documentos DUI/ID</label>
                <div class="aspect-video bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden border dark:border-slate-700">
                  <img v-if="dossier.customer.document_front_url" :src="dossier.customer.document_front_url" class="w-full h-full object-cover" />
                  <div v-else class="w-full h-full flex flex-col items-center justify-center text-[8px] text-slate-400 font-bold uppercase"><CreditCard class="w-4 h-4 mb-1" /> Frontal</div>
                </div>
                <div class="aspect-video bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden border dark:border-slate-700">
                  <img v-if="dossier.customer.document_back_url" :src="dossier.customer.document_back_url" class="w-full h-full object-cover" />
                  <div v-else class="w-full h-full flex flex-col items-center justify-center text-[8px] text-slate-400 font-bold uppercase"><CreditCard class="w-4 h-4 mb-1" /> Reverso</div>
                </div>
              </div>
            </div>

            <div class="lg:col-span-9 space-y-6">
              <div :class="sectionCardClasses">
                <h3 :class="sectionTitleClasses">Datos Maestros del Cliente</h3>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div><label :class="labelClasses">Nombre</label><p :class="dataClasses">{{ dossier.customer.full_name }}</p></div>
                  <div><label :class="labelClasses">Identificación</label><p :class="dataClasses" class="font-mono">{{ dossier.customer.identity_document }}</p></div>
                  <div><label :class="labelClasses">Nacimiento</label><p :class="dataClasses">{{ dossier.customer.birth_date }} ({{ calculateAge(dossier.customer.birth_date) }} años)</p></div>
                  <div><label :class="labelClasses">Género / Civil</label><p :class="dataClasses">{{ dossier.customer.gender === 'M' ? 'Masc.' : 'Fem.' }} • {{ dossier.customer.marital_status }}</p></div>
                  <div><label :class="labelClasses">Profesión</label><p :class="dataClasses">{{ dossier.customer.profession }}</p></div>
                  <div><label :class="labelClasses">Cargas</label><p :class="dataClasses">{{ dossier.customer.dependents_count }} Depend. / {{ dossier.customer.children_count }} Hijos</p></div>
                </div>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div :class="sectionCardClasses">
                  <h3 :class="sectionTitleClasses">Vivienda y Localización</h3>
                  <div class="space-y-4">
                    <div><label :class="labelClasses">Dirección</label><p :class="dataClasses" class="text-xs">{{ dossier.customer.exact_address || dossier.customer.community }}</p></div>
                    <div class="grid grid-cols-2 gap-2">
                      <div><label :class="labelClasses">Tipo Vivienda</label><p :class="dataClasses">{{ dossier.customer.housing_type }}</p></div>
                      <div><label :class="labelClasses">Estabilidad</label><p :class="dataClasses">{{ dossier.customer.years_living_there }} años</p></div>
                    </div>
                  </div>
                </div>
                <div :class="sectionCardClasses">
                  <h3 :class="sectionTitleClasses">Perfil Laboral</h3>
                  <div class="grid grid-cols-2 gap-4 text-slate-900 dark:text-slate-100">
                    <div><label :class="labelClasses">Empresa</label><p :class="dataClasses">{{ dossier.customer.company_name }}</p></div>
                    <div><label :class="labelClasses">Cargo</label><p :class="dataClasses">{{ dossier.customer.job_position }}</p></div>
                    <div><label :class="labelClasses">Jefe Directo</label><p :class="dataClasses">{{ dossier.customer.immediate_boss }}</p></div>
                    <div><label :class="labelClasses">Antigüedad</label><p :class="dataClasses">{{ dossier.customer.years_working }} años</p></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 2. ESTRUCTURA CRÉDITO -->
        <div v-show="activeTab === 'credit'" class="space-y-6 animate-fade-in">
          <div :class="sectionCardClasses" class="border-t-4 border-brand-600">
            <h3 :class="sectionTitleClasses">Estructura del Préstamo</h3>
            <div class="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div><label :class="labelClasses">Monto Desembolsado</label><p class="text-xl font-black font-mono dark:text-white">${{ formatNum(dossier.loan.amount) }}</p></div>
              <div><label :class="labelClasses">Saldo Capital Vivo</label><p class="text-xl font-black font-mono text-brand-600 dark:text-slate-400">${{ formatNum(dossier.loan.remaining_balance) }}</p></div>
              <div><label :class="labelClasses">Tasa / Interés</label><p class="text-sm font-bold dark:text-white">{{ dossier.loan.rate }}% ({{ dossier.product?.interest_type || 'Fija' }})</p></div>
              <div><label :class="labelClasses">Plazo Restante</label><p class="text-sm font-bold dark:text-white">{{ remainingInstallments }} de {{ dossier.loan.term }} Meses</p></div>
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="bg-slate-900 rounded-3xl p-8 text-white flex justify-between items-center shadow-xl">
              <div>
                <p class="text-[10px] font-black uppercase text-slate-400">Cuota Base Calculada</p>
                <p class="text-4xl font-black font-mono text-brand-400">${{ formatNum(currentMonthlyQuota) }}</p>
              </div>
              <BadgeDollarSign class="w-12 h-12 opacity-20" />
            </div>
            <div :class="sectionCardClasses" class="flex items-center justify-between dark:bg-slate-800">
              <div>
                <p class="text-[10px] font-black uppercase text-slate-400 dark:text-slate-500">Total Intereses Pagados</p>
                <p class="text-4xl font-black font-mono text-emerald-500">${{ formatNum(totalInterestPaid) }}</p>
              </div>
              <Activity class="w-10 h-10 opacity-20 dark:text-white" />
            </div>
          </div>
        </div>

        <!-- 3. HISTORIAL DE PAGOS (NUEVO) -->
        <div v-show="activeTab === 'history'" class="space-y-6 animate-fade-in">
          <div :class="sectionCardClasses" class="p-0 overflow-hidden border-none shadow-xl bg-white dark:bg-slate-800">
            <div class="px-6 py-4 bg-brand-600 text-white flex justify-between items-center">
                <h4 class="text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                    <History class="w-4 h-4 text-brand-400" /> Movimientos de Caja
                </h4>
            </div>
            <div class="overflow-x-auto">
              <table class="w-full text-sm text-left">
                <thead class="text-[10px] text-slate-400 uppercase font-black bg-slate-50 dark:bg-slate-950 border-b dark:border-slate-800">
                  <tr>
                    <th class="px-6 py-4">Fecha</th>
                    <th class="px-6 py-4">Operación</th>
                    <th class="px-6 py-4 text-right">Monto Total</th>
                    <th class="px-6 py-4 text-right text-emerald-600">A Capital</th>
                    <th class="px-6 py-4 text-right text-amber-600">A Interés</th>
                    <th class="px-6 py-4 text-right bg-slate-100 dark:bg-slate-900">Saldo Restante</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
                  <tr v-for="tx in dossier.transactions" :key="tx.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                    <td class="px-6 py-4 font-mono text-[11px] text-slate-700 dark:text-slate-300">{{ tx.transaction_date }}</td>
                    <td class="px-6 py-4">
                      <span :class="getTxBadge(tx.transaction_type)" class="px-2 py-0.5 rounded text-[8px] font-black uppercase">
                        {{ tx.transaction_type }}
                      </span>
                    </td>
                    <td class="px-6 py-4 text-right font-bold text-slate-700 dark:text-white">${{ formatNum(tx.total_amount) }}</td>
                    <td class="px-6 py-4 text-right font-bold text-emerald-600">${{ formatNum(tx.principal_applied) }}</td>
                    <td class="px-6 py-4 text-right font-bold text-amber-600">${{ formatNum(tx.interest_applied) }}</td>
                    <td class="px-6 py-4 text-right font-black font-mono bg-slate-50 dark:bg-slate-900/50 dark:text-white">${{ formatNum(tx.balance_after) }}</td>
                  </tr>
                  <tr v-if="!dossier.transactions || dossier.transactions.length === 0">
                    <td colspan="6" class="px-6 py-10 text-center text-slate-400 uppercase font-black text-[10px]">No hay transacciones registradas para este crédito</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- 4. PLAN DE PAGOS -->
        <div v-show="activeTab === 'payments'" class="animate-fade-in">
           <div :class="sectionCardClasses" class="p-0 overflow-hidden border-none shadow-xl bg-white dark:bg-slate-800">
              <div class="px-6 py-4 bg-brand-600 dark:bg-brand-600 text-white flex justify-between items-center">
                  <h4 class="text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                      <Calendar class="w-4 h-4" /> Proyección y Vencimientos
                  </h4>
              </div>
              <div class="overflow-x-auto">
                <table class="w-full text-sm text-left">
                  <thead class="text-[10px] text-slate-500 dark:text-slate-400 uppercase font-black bg-slate-100 dark:bg-slate-950 border-b dark:border-slate-700">
                    <tr><th class="px-6 py-4">Nº</th><th class="px-6 py-4">Vencimiento</th><th class="px-6 py-4 text-right">Capital</th><th class="px-6 py-4 text-right">Interés</th><th class="px-6 py-4 text-right font-bold ">Total</th><th class="px-6 py-4 text-center">Estado</th></tr>
                  </thead>
                  <tbody class="divide-y divide-slate-100 dark:divide-slate-700">
                    <tr v-for="p in dossier.payment_plans" :key="p.installment_number"
                      :class="[p.status === 'PAID' ? 'opacity-50 bg-slate-50/50 dark:bg-slate-900/50' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:text-slate-200']">
                      <td class="px-6 py-3 font-bold">{{ p.installment_number }}</td>
                      <td class="px-6 py-3">{{ p.due_date }}</td>
                      <td class="px-6 py-3 text-right font-mono">${{ formatNum(p.capital) }}</td>
                      <td class="px-6 py-3 text-right font-mono">${{ formatNum(p.interest) }}</td>
                      <td class="px-6 py-3 text-right font-mono font-black">${{ formatNum(p.total_amount) }}</td>
                      <td class="px-6 py-3 text-center">
                        <span :class="p.status === 'PAID' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'" class="px-2 py-0.5 rounded text-[8px] font-black uppercase">
                          {{ p.status }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
           </div>
        </div>
        <div v-show="activeTab === 'theoretical'" class="animate-fade-in">
          <div :class="sectionCardClasses" class="p-0 overflow-hidden border-none shadow-xl bg-white dark:bg-slate-800">
              <div class="px-6 py-4 bg-brand-600 dark:bg-brand-600  text-white flex justify-between items-center">
                  <h4 class="text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                      <Calendar class="w-4 h-4" /> Proyección Teórica
                  </h4>
              </div>
              <div class="overflow-x-auto">
                <table class="w-full text-sm text-left">
                  <thead class="text-[10px] text-slate-500 dark:text-slate-400 uppercase font-black bg-slate-100 dark:bg-slate-950 border-b dark:border-slate-700">
                    <tr>
                      <th class="px-6 py-4">Nº</th>
                      <th class="px-6 py-4">Vencimiento</th>
                      <th class="px-6 py-4 text-right">Capital</th>
                      <th class="px-6 py-4 text-right">Interés</th>
                      <th class="px-6 py-4 text-right font-bold text-slate-600">Total</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-100 dark:divide-slate-700">
                    <tr v-for="p in dossier.theoretical_plans" :key="p.installment_number" class="hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:text-slate-200">
                      <td class="px-6 py-3 font-bold">{{ p.installment_number }}</td>
                      <td class="px-6 py-3">{{ p.due_date }}</td>
                      <td class="px-6 py-3 text-right font-mono">${{ formatNum(p.capital) }}</td>
                      <td class="px-6 py-3 text-right font-mono">${{ formatNum(p.interest) }}</td>
                      <td class="px-6 py-3 text-right font-mono font-black text-slate-600">${{ formatNum(p.total_amount) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
          </div>
        </div>
        <!-- 5. ANÁLISIS DE FLUJO -->
        <div v-show="activeTab === 'balance'" class="space-y-6 animate-fade-in">
          <div :class="sectionCardClasses">
            <h3 :class="sectionTitleClasses">Evaluación de la Actividad Comercial</h3>
            <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <div class="md:col-span-2"><label :class="labelClasses">Nombre del Negocio</label><p :class="dataClasses">{{ dossier.evaluation.business_name }}</p></div>
              <div><label :class="labelClasses">Estabilidad</label><p :class="dataClasses">{{ getGlobalText(dossier.evaluation.business_stability, 9) }}</p></div>
              <div><label :class="labelClasses">Tipo/Giro</label><p :class="dataClasses">{{ getGlobalText(dossier.evaluation.business_type, 8) }}</p></div>
            </div>
            <div class="p-4 bg-slate-100 dark:bg-slate-800 rounded-2xl border dark:border-slate-700 mb-6">
              <label :class="labelClasses">Descripción de la Actividad</label>
              <p class="text-xs font-medium dark:text-slate-300 leading-relaxed">{{ dossier.evaluation.business_description }}</p>
            </div>

            <!-- VISOR DE FOTOS DEL NEGOCIO -->
            <div class="mb-6 grid grid-cols-2 gap-4">
              <div v-if="dossier.evaluation.photo_url_1" class="aspect-video rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
                 <img :src="dossier.evaluation.photo_url_1" class="w-full h-full object-cover hover:scale-105 transition-transform" />
              </div>
              <div v-if="dossier.evaluation.photo_url_2" class="aspect-video rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
                 <img :src="dossier.evaluation.photo_url_2" class="w-full h-full object-cover hover:scale-105 transition-transform" />
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div class="space-y-4">
                <label :class="labelClasses">Ventas Auditadas (Semana Tipo)</label>
                <div class="grid grid-cols-7 gap-1">
                  <div v-for="day in daysMap" :key="day.key" class="text-center p-2 bg-white dark:bg-slate-900 border dark:border-slate-700 rounded-xl">
                    <p class="text-[8px] font-black text-slate-400 uppercase">{{ day.label }}</p>
                    <p class="text-[10px] font-mono font-bold dark:text-white">${{ formatNum(dossier.evaluation['inc_'+day.key]) }}</p>
                    <div class="mt-1 text-[7px] font-black uppercase p-0.5 rounded" :class="getDayStatusClass(day.labelFull, dossier.evaluation)">{{ getDayShortLabel(day.labelFull, dossier.evaluation) }}</div>
                  </div>
                </div>
                <div class="grid grid-cols-2 gap-4">
                  <div class="p-3 bg-emerald-50 dark:bg-emerald-900/10 rounded-xl border border-emerald-100 dark:border-emerald-800">
                    <label :class="labelClasses" class="text-emerald-700">Ingreso Semanal</label>
                    <p class="text-lg font-mono font-black text-emerald-600">${{ formatNum(dossier.evaluation.total_weekly_income) }}</p>
                  </div>
                  <div class="p-3 bg-emerald-50 dark:bg-emerald-900/10 rounded-xl border border-emerald-100 dark:border-emerald-800">
                    <label :class="labelClasses" class="text-emerald-700">Ingreso Mensual</label>
                    <p class="text-lg font-mono font-black text-emerald-600">${{ formatNum(dossier.evaluation.total_monthly_income) }}</p>
                  </div>
                </div>
              </div>
              <div class="space-y-4">
                <label :class="labelClasses">Egresos y Gastos Operativos</label>
                <div class="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto custom-scrollbar pr-2">
                  <div v-for="(exp, i) in parseJSON(dossier.evaluation.business_expenses_json)" :key="i"
                    class="flex justify-between p-2.5 bg-white dark:bg-slate-900 border dark:border-slate-700 rounded-xl">
                    <span class="text-[10px] font-bold text-slate-600 dark:text-slate-400 uppercase">{{ exp.type }}: {{ exp.description }}</span>
                    <span class="text-sm font-mono font-bold text-red-500">-${{ formatNum(exp.amount) }}</span>
                  </div>
                </div>
                <div class="grid grid-cols-2 gap-4 mt-2">
                  <div class="p-3 bg-red-50 dark:bg-red-900/10 rounded-xl border border-red-100 dark:border-red-800">
                    <label :class="labelClasses" class="text-red-700">Costo de Venta</label>
                    <p class="text-sm font-mono font-black text-red-600">-${{ formatNum(dossier.evaluation.estimated_monthly_cost) }}</p>
                  </div>
                  <div class="p-3 bg-red-50 dark:bg-red-900/10 rounded-xl border border-red-100 dark:border-red-800">
                    <label :class="labelClasses" class="text-red-700">Deuda Externa</label>
                    <p class="text-sm font-mono font-black text-red-600">-${{ formatNum(dossier.evaluation.monthly_debt_payment) }}</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="mt-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border dark:border-slate-700">
              <label :class="labelClasses">Observaciones del Analista de Campo</label>
              <p class="text-xs font-medium dark:text-slate-400 italic">"{{ dossier.evaluation.observations || 'Sin observaciones registradas.' }}"</p>
            </div>
          </div>
        </div>

        <!-- 6. GARANTÍAS -->
        <div v-show="activeTab === 'collaterals'" class="animate-fade-in grid grid-cols-1 md:grid-cols-2 gap-6 text-slate-800 dark:text-slate-200">
          <div v-for="col in dossier.collaterals" :key="col.id" :class="sectionCardClasses" class="border-l-8 border-brand-600 shadow-lg dark:bg-slate-800 dark:border-slate-700">
             <div class="flex justify-between items-start mb-6">
              <div>
                <span class="px-2 py-0.5 bg-brand-100 dark:bg-brand-900 text-brand-700 dark:text-brand-300 text-[9px] font-black uppercase rounded">{{ col.collateral_type }}</span>
                <h4 class="text-sm font-black mt-2 uppercase tracking-tight">{{ col.pledge_type || col.mortgage_type || 'Garantía Registrada' }}</h4>
                <p class="text-[9px] font-bold text-slate-400 mt-1 uppercase">Propietario: {{ col.owner_name || dossier.customer.full_name }}</p>
              </div>
              <div class="text-right">
                <p class="text-[9px] font-bold text-slate-400 uppercase">Valor Avalúo</p>
                <p class="text-xl font-mono font-black text-emerald-600">${{ formatNum(col.guarantee_amount) }}</p>
                <div class="mt-2 text-[8px] font-black px-1.5 py-0.5 bg-brand-600 text-white rounded">COBERTURA: {{ col.coverage_percentage }}%</div>
              </div>
            </div>

            <!-- VISOR DE FOTOS DE GARANTÍA -->
            <div class="grid grid-cols-2 gap-2 mb-4">
              <div v-if="col.photo_url_1" class="h-24 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100">
                 <img :src="col.photo_url_1" class="w-full h-full object-cover" />
              </div>
              <div v-if="col.photo_url_2" class="h-24 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100">
                 <img :src="col.photo_url_2" class="w-full h-full object-cover" />
              </div>
              <div v-if="!col.photo_url_1 && !col.photo_url_2" class="col-span-2 py-4 text-center border-2 border-dashed rounded-xl text-slate-300 text-[8px] font-black uppercase">
                Sin evidencia fotográfica
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4 text-[10px] mb-4">
              <div class="p-2 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <span class="text-slate-400 block uppercase font-bold">Registro</span>
                <span class="font-bold">{{ col.registration_number || 'N/A' }} ({{ col.registration_date || 'Sin fecha' }})</span>
              </div>
              <div class="p-2 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <span class="text-slate-400 block uppercase font-bold">Estado Legal</span>
                <span class="font-bold text-emerald-600">{{ col.status }}</span>
              </div>
            </div>
          </div>
          <div v-if="!dossier.collaterals || dossier.collaterals.length === 0" class="col-span-full py-10 text-center">
            <Key class="w-12 h-12 mx-auto text-slate-200 dark:text-slate-700 mb-4" />
            <p class="text-slate-400 text-xs font-black uppercase">Este crédito no posee garantías reales registradas.</p>
          </div>
        </div>

      </div>

      <div class="px-6 py-4 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center z-20">
        <div class="flex items-center gap-3">
          <p class="text-[9px] text-slate-400 font-mono font-bold uppercase tracking-widest">VISOR DE SOLO LECTURA</p>

        </div>
        <div class="flex gap-3">
          <button @click="handlePrintSummary" type="button" :class="btnPrimary">
            <Printer/>
            Imprimir Resumen</button>
          <button @click="printPagare" type="button" :class="btnPrimary">
            <Printer/>
            Imprimir Pagaré</button>
          <button @click="$emit('close')" type="button" :class="btnSecondary">Cerrar Visor</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, handleError } from 'vue';
import {
  X, Loader2, User, Activity, CreditCard, BadgeDollarSign,
  History, Calendar, Printer, Key
} from 'lucide-vue-next';
import apiClient from '@/api/client';
import { useToast } from '@/composables/useToast';
import { SecureStorage } from '@/utils/secureStorage';
import { useAccountStatement } from '@/composables/reports/useAccountStatement';
import { useInformativeSummary } from '@/composables/reports/useInformativeSummary';
import { usePromissoryNote } from '@/composables/contracts/usePromissoryNote';
const props = defineProps({ show: Boolean, loanId: String });
const emit = defineEmits(['close']);
const { showError } = useToast();
const { generateStatement } = useAccountStatement();
const { generatePromissoryNote } = usePromissoryNote();
const { generateSummary } = useInformativeSummary();
const isLoadingDossier = ref(false);
const dossier = ref(null);
const activeTab = ref('customer');
const globalCatalog = ref([]); // Almacena el catálogo de Globals
const loanData = ref({});
// CLASES ESTÉTICAS (Consistentes)
const dataClasses = "text-sm font-bold text-slate-900 dark:text-white uppercase";
const labelClasses = "text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase mb-1 block tracking-widest";
const sectionCardClasses = "bg-slate-50/50 dark:bg-slate-800/40 p-6 rounded-3xl border border-slate-100 dark:border-slate-700 shadow-sm";
const sectionTitleClasses = "text-xs font-black text-slate-900 dark:text-white flex items-center gap-2 mb-6 uppercase tracking-tight";
const btnPrimary = "px-6 py-2.5 text-sm font-bold text-white bg-brand-600 rounded-xl hover:bg-brand-700 shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70 w-full sm:w-auto";
const btnSecondary = "px-6 py-3 text-[10px] font-black text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors uppercase tracking-widest";

const tabs = [
  { id: 'customer', name: 'Perfil Cliente' },
  { id: 'credit', name: 'Crédito' },
  { id: 'history', name: 'Movimientos' },
  { id: 'payments', name: 'Plan Pagos' },
  { id: 'theoretical', name: 'Plan Teórico' },
  { id: 'balance', name: 'Análisis Flujo' },
  { id: 'collaterals', name: 'Garantías' }
];

const daysMap = [
  { key: 'monday', label: 'Lun', labelFull: 'Lunes' }, { key: 'tuesday', label: 'Mar', labelFull: 'Martes' }, { key: 'wednesday', label: 'Mié', labelFull: 'Miércoles' },
  { key: 'thursday', label: 'Jue', labelFull: 'Jueves' }, { key: 'friday', label: 'Vie', labelFull: 'Viernes' }, { key: 'saturday', label: 'Sáb', labelFull: 'Sábado' },
  { key: 'sunday', label: 'Dom', labelFull: 'Domingo' }
];

const formatNum = (val) => Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const parseJSON = (str) => { try { return typeof str === 'string' ? JSON.parse(str) : (str || []); } catch { return []; } };

const calculateAge = (date) => {
  if(!date) return 'N/A';
  const birth = new Date(date);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  if (now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())) age--;
  return age;
};

// TRADUCTOR DE GLOBALS
const getGlobalText = (value, type) => {
    const item = globalCatalog.value.find(g => Number(g.type) === type && String(g.value) === String(value));
    return item ? item.text.toUpperCase() : (value || 'N/D');
};

// CÁLCULOS ANALÍTICOS
const totalInterestPaid = computed(() => {
    if (!dossier.value?.transactions) return 0;
    return dossier.value.transactions.reduce((sum, tx) => sum + Number(tx.interest_applied || 0), 0);
});

const remainingInstallments = computed(() => {
    if (!dossier.value?.payment_plans) return 0;
    return dossier.value.payment_plans.filter(p => p.status !== 'PAID').length;
});

const currentMonthlyQuota = computed(() => {
    if (!dossier.value?.loan || !dossier.value?.payment_plans) return 0;
    const plan = dossier.value.payment_plans.find(p => p.status === 'PENDING');
    return plan ? Number(plan.total_amount) : Number(dossier.value.loan.estimated_quota || 0);
});

const getDayStatusClass = (day, evalData) => {
  const good = parseJSON(evalData.good_days_json);
  const regular = parseJSON(evalData.regular_days_json);
  if (good.includes(day)) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
  if (regular.includes(day)) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
  return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
};

const getDayShortLabel = (day, evalData) => {
  const good = parseJSON(evalData.good_days_json);
  const regular = parseJSON(evalData.regular_days_json);
  if (good.includes(day)) return 'Bueno';
  if (regular.includes(day)) return 'Regular';
  return 'Malo';
};

const calculateDSCR = () => {
  if (!dossier.value?.evaluation) return "0.00";
  const monthlyNet = Number(dossier.value.evaluation.final_daily_available || 0) * 30;
  const quota = currentMonthlyQuota.value || 1;
  return (monthlyNet / quota).toFixed(2);
};

const capacityColor = computed(() => {
  const ratio = Number(calculateDSCR());
  if (ratio >= 1.5) return { text: 'text-emerald-500' };
  if (ratio >= 1.0) return { text: 'text-amber-500' };
  return { text: 'text-red-500' };
});

const getTxBadge = (type) => {
    if (type === 'PAYMENT') return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
    if (type === 'DISBURSEMENT') return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    return 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
};

watch(() => props.show, async (val) => {
  if (val && props.loanId) {
    activeTab.value = 'customer';
    dossier.value = null;
    isLoadingDossier.value = true;

    try {
      const user = SecureStorage.getItem('llevo_user');

      // PASO 1: Obtener el préstamo primero (Obligatorio para tener los IDs)
      const [loanRes, globalsRes] = await Promise.all([
        apiClient.get(`/loans/${props.loanId}`),
        apiClient.get(`/globals`)
      ]);

      globalCatalog.value = globalsRes.data.data || globalsRes.data;

      // Asignamos al ref reactivo usando .value
      const currentLoan = Array.isArray(loanRes.data) ? loanRes.data[0] : (loanRes.data.data || loanRes.data);
      loanData.value = currentLoan;

      // VALIDACIÓN: Si no hay IDs, detenemos para evitar errores 404
      if (!loanData.value?.customer_id || !loanData.value?.product_id) {
        throw new Error("Datos de origen del préstamo incompletos.");
      }

      // PASO 2: Carga paralela del resto del expediente usando loanData.value
      const [customerRes, evaluationRes, collateralsRes, planRes,theoreticalRes, transactionsRes, productRes] = await Promise.all([
        apiClient.get(`/customers/${loanData.value.customer_id}`),
        apiClient.get(`/financial_evaluations?customer_id=${loanData.value.customer_id}`),
        apiClient.get(`/collaterals?loan_id=${props.loanId}&company_id=${user.company}`),
        apiClient.get(`/payment_plans?loan_id=${props.loanId}`).catch(() => ({ data: [] })),
        apiClient.get(`/payment_plans_theoretical?loan_id=${props.loanId}`).catch(() => ({ data: [] })),
        apiClient.get(`/loan_transactions?loan_id=${props.loanId}`).catch(() => ({ data: [] })),
        apiClient.get(`/credit_products/${loanData.value.product_id}`)
      ]);

      const custRaw = customerRes.data?.data || customerRes.data;
      const evalData = Array.isArray(evaluationRes.data) ? evaluationRes.data[0] : (evaluationRes.data?.data || evaluationRes.data);
      const collatData = collateralsRes.data?.data || collateralsRes.data || [];
      const planData = planRes.data?.data || planRes.data || [];
      const theoData = theoreticalRes.data?.data || theoreticalRes.data || [];
      const txs = Array.isArray(transactionsRes.data) ? transactionsRes.data : (transactionsRes.data?.data || []);
      const prodData = productRes.data?.data || productRes.data;

      // Armado del Dossier usando loanData.value
      dossier.value = {
        loan: {
          ...loanData.value,
          estimated_quota: Number(loanData.value.total_amount || 0) / Number(loanData.value.term || 1)
        },
        customer: {
          ...custRaw,
          full_name: `${custRaw.first_name || ''} ${custRaw.middle_name || ''} ${custRaw.last_name || ''} ${custRaw.second_last_name || ''}`.replace(/\s+/g, ' ').trim()
        },
        evaluation: evalData || {},
        collaterals: collatData,
        payment_plans: planData.sort((a, b) => a.installment_number - b.installment_number),
        theoretical_plans: theoData.sort((a, b) => a.installment_number - b.installment_number),
        transactions: txs.sort((a, b) => new Date(a.transaction_date) - new Date(b.transaction_date)),
        product: prodData
      };
    } catch (e) {
      console.error("Error en expediente integral:", e);
      showError('Error al recuperar expediente integral.');
      emit('close');
    } finally {
      isLoadingDossier.value = false;
    }
  }
});

const handlePrint = () => {
  if(dossier.value){
    generateStatement(
      dossier.value
    );
  }
};

const printPagare = () => {
  if (dossier.value) {
    const contractType = dossier.value.product?.contract_type;
    if(contractType==1){
      generatePromissoryNote(dossier.value);
    }else{
      generatePromissoryNote(dossier.value);
    }

  }
};

const handlePrintSummary = () => {
  console.log(dossier.value)
  if(dossier.value){
    generateSummary(
      dossier.value,{
        showPerfil: true,
        showProducto: true,
        showResolucion: true,
        showEvaluacion: true,
        showGarantias: true,
        showPlanTeorico: true,
        showPlanActivo: true,
        showTransacciones: true,
        showAnexos: true,
      },
      globalCatalog.value,
      'EXPEDIENTE INTEGRAL'
    );
  }
};
</script>

<style scoped>
@keyframes fade-in { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); } }
.animate-fade-in { animation: fade-in 0.2s ease-out forwards; }
.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #334155; }
</style>